import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid } from "@mui/x-data-grid";
import API from "../../../../api";
import { styles } from "../../../../theme";
import { useNavigate } from "react-router-dom";

const tipoMap = {
  0: { label: "HIGH", bg: styles?.logColors?.[0] },
  1: { label: "CRITICAL", bg: styles?.logColors?.[1] },
  2: { label: "ALERT", bg: styles?.logColors?.[2] },
  3: { label: "MOVEMENTS", bg: styles?.logColors?.[3] },
};

const severityPriority = {
  1: 0,
  0: 1,
  2: 2,
  3: 3,
};

const sortTypesByPriority = (types) => {
  const arr = Array.isArray(types) ? types : [];
  return [...arr].sort((a, b) => {
    const pa = severityPriority[a] ?? 999;
    const pb = severityPriority[b] ?? 999;
    return pa - pb;
  });
};

const uniqueTypesMax4 = (events) => {
  const arr = Array.isArray(events) ? events : [];
  const uniq = Array.from(
    new Set(arr.map((ev) => ev?.tipo).filter((t) => t != null))
  );
  return sortTypesByPriority(uniq).slice(0, 4);
};

const uniqueIpSources = (events) => {
  const arr = Array.isArray(events) ? events : [];
  return Array.from(
    new Set(
      arr
        .map((ev) => ev?.ipSource)
        .filter((ip) => typeof ip === "string" && ip.trim().length > 0)
        .map((ip) => ip.trim())
    )
  );
};

const uniqueIpDestinations = (events) => {
  const arr = Array.isArray(events) ? events : [];
  return Array.from(
    new Set(
      arr
        .map((ev) => ev?.ipDestination)
        .filter((ip) => typeof ip === "string" && ip.trim().length > 0)
        .map((ip) => ip.trim())
    )
  );
};

const compareRows = (a, b) => {
  if (a.totalCount !== b.totalCount) return a.totalCount - b.totalCount;

  const aFirst = a.tipi?.[0];
  const bFirst = b.tipi?.[0];

  const pa = severityPriority[aFirst] ?? 999;
  const pb = severityPriority[bFirst] ?? 999;

  if (pa !== pb) return pa - pb;

  return String(a.dettaglio || "").localeCompare(String(b.dettaglio || ""));
};

const normalizeIp = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const buildAnomalyPairSet = (anomaliesTop, anomaliesRare) => {
  const all = [
    ...(Array.isArray(anomaliesTop) ? anomaliesTop : []),
    ...(Array.isArray(anomaliesRare) ? anomaliesRare : []),
  ];

  const pairSet = new Set();

  all.forEach((item) => {
    const ipA = normalizeIp(item?.ipA);
    const ipB = normalizeIp(item?.ipB);
    if (!ipA || !ipB) return;

    pairSet.add(`${ipA}__${ipB}`);
    pairSet.add(`${ipB}__${ipA}`);
  });

  return pairSet;
};

const hasRowAnomalyMatch = (events, anomalyPairSet) => {
  const arr = Array.isArray(events) ? events : [];
  if (!arr.length || !anomalyPairSet?.size) return false;

  return arr.some((ev) => {
    const ipSource = normalizeIp(ev?.ipSource);
    const ipDestination = normalizeIp(ev?.ipDestination);
    if (!ipSource || !ipDestination) return false;

    return anomalyPairSet.has(`${ipSource}__${ipDestination}`);
  });
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const RarestLogsUnified = ({
  multiSecurityView = false,
  fillHeight = false,
  anomaliesTop,
  anomaliesRare,
}) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [paginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [rowHeight, setRowHeight] = useState(52);

  const gridWrapperRef = useRef(null);
  const multiView = multiSecurityView === true;

  const anomalyPairSet = useMemo(() => {
    return buildAnomalyPairSet(anomaliesTop, anomaliesRare);
  }, [anomaliesTop, anomaliesRare]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    API.getLogsByDettaglio({ multiView })
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];

        const mapped = arr
          .map((x, idx) => {
            const events = Array.isArray(x?.events) ? x.events : [];

            return {
              id: `${x?.dettaglio ?? "missing"}__${idx}`,
              dettaglio: x?.dettaglio ?? "(missing)",
              totalCount: Number(x?.totalCount ?? 0) || 0,
              tipi: uniqueTypesMax4(events),
              ipsSource: uniqueIpSources(events),
              ipsDestination: uniqueIpDestinations(events),
              hasAnomalyMatch: hasRowAnomalyMatch(events, anomalyPairSet),
            };
          })
          .sort(compareRows);

        if (alive) setRows(mapped);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error || err?.message || "Loading error.";
        if (alive) setError(msg);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [multiView, anomalyPairSet]);

  const recalcRowHeight = useCallback(() => {
    const el = gridWrapperRef.current;
    if (!el) return;

    const totalHeight = el.clientHeight;
    if (!totalHeight) return;

    const columnHeaderHeight = 56;
    const footerHeight = 52;
    const scrollbarBuffer = 8;

    const availableBodyHeight =
      totalHeight - columnHeaderHeight - footerHeight - scrollbarBuffer;

    const rowsPerPage = paginationModel.pageSize || 10;

    const nextRowHeight = clamp(
      Math.floor(availableBodyHeight / rowsPerPage),
      44,
      110
    );

    setRowHeight(nextRowHeight);
  }, [paginationModel.pageSize]);

  useEffect(() => {
    recalcRowHeight();
  }, [recalcRowHeight, rows.length, loading, error]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      recalcRowHeight();
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [recalcRowHeight]);

  const handleRowClick = useCallback(
    (params) => {
      const row = params?.row || {};

      const firstSeverity =
        Array.isArray(row.tipi) && row.tipi.length
          ? (tipoMap[row.tipi[0]]?.label ?? "unknown").toLowerCase()
          : null;

      const dateToObj = new Date();
      const dateFromObj = new Date(dateToObj.getTime() - 24 * 60 * 60 * 1000);

      const dateTo = dateToObj.toISOString();
      const dateFrom = dateFromObj.toISOString();

      if (sessionStorage.getItem("type") === "user") {
        navigate(
          `../security/${firstSeverity}?exact_details=${encodeURIComponent(
            row.dettaglio
          )}&dateFrom=${encodeURIComponent(
            dateFrom
          )}&dateTo=${encodeURIComponent(dateTo)}`
        );
      } else {
        navigate(
          `../myC/${firstSeverity}?exact_details=${encodeURIComponent(
            row.dettaglio
          )}&dateFrom=${encodeURIComponent(
            dateFrom
          )}&dateTo=${encodeURIComponent(dateTo)}`
        );
      }
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "totalCount",
        headerName: "Count",
        type: "number",
        width: 90,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "dettaglio",
        headerName: "Detail",
        minWidth: 250,
        renderCell: (params) => (
          <Tooltip title={params.value} placement="top-start">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{ color: "#eaeaea" }}
              >
                {params.value}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "tipi",
        headerName: "Severity",
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          const arr = Array.isArray(params.value) ? params.value : [];
          if (!arr.length) {
            return <Chip label="UNKNOWN" size="small" variant="outlined" />;
          }

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                alignItems: "center",
                py: 0.75,
              }}
            >
              {arr.map((t) => {
                const info = tipoMap[t];
                return (
                  <Chip
                    key={t}
                    label={info?.label ?? "UNKNOWN"}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: info?.bg,
                      color: "#fff",
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        field: "ipsSource",
        headerName: "Ips Source",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const arr = Array.isArray(params.value) ? params.value : [];
          if (!arr.length) {
            return <Chip label="N/A" size="small" variant="outlined" />;
          }

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                alignItems: "center",
                py: 0.75,
              }}
            >
              {arr.slice(0, 6).map((ip) => (
                <Chip key={ip} label={ip} size="small" variant="outlined" />
              ))}
              {arr.length > 6 && (
                <Chip label={`+${arr.length - 6}`} size="small" />
              )}
            </Box>
          );
        },
      },
      {
        field: "ipsDestination",
        headerName: "Ips Destination",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const arr = Array.isArray(params.value) ? params.value : [];
          if (!arr.length) {
            return <Chip label="N/A" size="small" variant="outlined" />;
          }

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                alignItems: "center",
                py: 0.75,
              }}
            >
              {arr.slice(0, 6).map((ip) => (
                <Chip key={ip} label={ip} size="small" variant="outlined" />
              ))}
              {arr.length > 6 && (
                <Chip label={`+${arr.length - 6}`} size="small" />
              )}
            </Box>
          );
        },
      },
    ],
    []
  );

  return (
    <Paper
      sx={{
        p: 2,
        background: "#1e1e1e",
        borderRadius: "15px",
        height: fillHeight ? "100%" : 520,
        minHeight: 420,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
          Rarest logs
        </Typography>

        <Tooltip title="Highlighted rows were also detected in Tiresia Anomalies">
          <IconButton size="small" sx={{ color: "#555" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!error && (
        <Box
          ref={gridWrapperRef}
          sx={{ flex: 1, minHeight: 0, width: "100%" }}
        >
          <DataGrid
            density="compact"
            rows={rows}
            columns={columns}
            loading={loading}
            onRowClick={handleRowClick}
            disableRowSelectionOnClick
            hideFooter
            rowHeight={rowHeight}
            columnHeaderHeight={56}
            localeText={{ noRowsLabel: "No data available." }}
            getRowClassName={(params) =>
              params?.row?.hasAnomalyMatch ? "rarest-row-anomaly-match" : ""
            }
            slotProps={{ loadingOverlay: { variant: "skeleton" } }}
            sx={{
              height: "100%",
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .rarest-row-anomaly-match": { backgroundColor: "rgba(255, 235, 59, 0.16)" },
              "& .rarest-row-anomaly-match:hover": { backgroundColor: "rgba(255, 235, 59, 0.24)" },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default RarestLogsUnified;